<template>
  <sf-modal-portal
    modal-id="complete-meeting-modal"
    size="l"
    @close="closeModal"
    @opened="openModal"
  >
    <template #title>
      {{ titleLabel }}
    </template>

    <template #messages>
      <SfModalMessage v-if="errorMessage" variant="error">
        {{ errorMessage }}
      </SfModalMessage>
    </template>

    <template #body>
      <div class="tw-flex tw-gap-4">
        <div class="tw-flex-none tw-w-30">
          <label for="meeting-date" class="tw-block tw-mb-1 tw-font-medium">{{
            dateLabel
          }}</label>
          <div class="tw-flex tw-items-center">
            <DatePicker
              v-model="meetingDate"
              name="meeting-date"
              :icon="true"
              class="!tw-leading-4"
            />
          </div>
        </div>
        <div class="tw-flex-1">
          <label for="meeting-title" class="tw-block tw-mb-1 tw-font-medium">
            {{ meetingTitleLabel }}
          </label>
          <SfInput
            v-model="meetingTitle"
            component-type="input"
            name="meeting-title"
            class="sf-form-input"
            data-testid="complete-meeting-modal-title"
            :placeholder="optionalPlaceholder"
            :focused="true"
          />
        </div>
      </div>

      <div class="tw-mt-6 tw-mb-1">
        <label for="meeting-summary" class="tw-block tw-mb-1 tw-font-medium">
          {{ meetingSummaryLabel }}
        </label>
        <div class="tw-w-full tw-relative tw-bg-sf-snow">
          <SfInput
            v-model="meetingSummary"
            data-testid="complete-meeting-modal-summary"
            component-type="textarea"
            name="meeting-summary"
            rows="11"
          />
          <div
            v-if="summaryLoading"
            class="
              tw-absolute
              tw-inset-0
              tw-flex
              tw-items-center
              tw-justify-center
              tw-z-10
              tw-rounded-lg
              tw-pointer-events-auto
            "
          >
            <div class="tw-flex tw-flex-col tw-items-center tw-gap-3">
              <div
                class="
                  tw-w-[54px]
                  tw-h-[54px]
                  tw-rounded-full
                  tw-bg-sf-snow
                  tw-flex
                  tw-items-center
                  tw-justify-center
                "
              >
                <div
                  class="fa fa-wand-magic-sparkles tw-text-sf-ai tw-text-2xl"
                />
              </div>
              <div
                v-dompurify-html="aiSummaryLoadingInfo"
                class="tw-font-medium tw-text-center"
              />
              <SfLoadingDots />
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #messages-bottom>
      <sf-modal-message v-if="!edit" variant="neutral">
        {{ infoText }}
      </sf-modal-message>
    </template>

    <template #footer>
      <SfButton variant="neutral" @click="closeModal">
        {{ cancelLabel }}
      </SfButton>
      <SfButton
        data-testid="complete-meeting-modal-submit"
        variant="success"
        :disabled="summaryLoading"
        @click="handleCompleteMeeting"
      >
        {{ confirmLabel }}
      </SfButton>
    </template>
  </sf-modal-portal>
</template>

<script setup lang="ts">
import type { Ref } from 'vue'
import { computed, ref, nextTick } from 'vue'

import api from '../api'

import {
  SfModalMessage,
  SfModalPortal,
} from '@app/shared/vue/components/sf-modal'
import { SfInput } from '@app/shared/vue/components/sf-input'
import DatePicker from '@app/shared/vue/components/form/date-picker.vue'
import SfButton from '../../../../../shared/vue/components/sf-button/sf-button.vue'
import SfLoadingDots from '@app/shared/vue/components/loaders/sf-loading-dots.vue'

import { SfTime } from '@app/adapters/time'
import { SfLog } from '@app/adapters/logger'
import useI18n from '@app/shared/vue/composables/use-I18n'

const I18n = useI18n()

defineOptions({
  name: 'CompleteMeetingModal',
})

interface Props {
  remarkIds: number[]
  meetingId?: number | null
  title?: string | null
  summary?: string | null
  date?: string
  edit?: boolean
}

const props = defineProps<Props>()

const meetingTitle: Ref<string | null> = ref('')
const meetingSummary: Ref<string | null> = ref('')
const meetingDate: Ref<string | null> = ref(null)
const errorMessage: Ref<string | null> = ref(null)
const summaryLoading: Ref<boolean> = ref(false)

const titleLabel = computed(() =>
  props.edit
    ? window.I18n.t(
        'client_meetings.meeting_preparation.complete_meeting_modal.edit_title'
      )
    : window.I18n.t(
        'client_meetings.meeting_preparation.complete_meeting_modal.title'
      )
)

const infoText = I18n.t(
  'client_meetings.meeting_preparation.complete_meeting_modal.info_text'
)
const cancelLabel = I18n.t('common.cancel')

const confirmLabel = computed(() =>
  props.edit
    ? window.I18n.t(
        'client_meetings.meeting_preparation.complete_meeting_modal.edit'
      )
    : window.I18n.t(
        'client_meetings.meeting_preparation.complete_meeting_modal.complete'
      )
)

const dateLabel = I18n.t(
  'client_meetings.meeting_preparation.complete_meeting_modal.date'
)
const meetingTitleLabel = I18n.t(
  'client_meetings.meeting_preparation.complete_meeting_modal.meeting_title'
)
const meetingSummaryLabel = I18n.t(
  'client_meetings.meeting_preparation.complete_meeting_modal.meeting_summary'
)

const aiSummaryLoadingInfo = I18n.t(
  'client_meetings.meeting_preparation.complete_meeting_modal.ai_summary_loading_info'
)

const optionalPlaceholder = I18n.t('common.optional')

const meetingIdRef = ref<number | null>(null)
const remarkIdsRef = ref<number[]>([])

const openModal = async (): Promise<void> => {
  await nextTick()

  remarkIdsRef.value = [...(props.remarkIds || [])]
  meetingIdRef.value = props.meetingId ?? null

  if (props.title) {
    meetingTitle.value = props.title
  }

  if (props.summary) {
    meetingSummary.value = props.summary
  }

  if (props.date) {
    meetingDate.value = props.date
  } else {
    meetingDate.value = SfTime(new Date()).format('DD/MM/YYYY')
  }

  errorMessage.value = null

  if (!meetingSummary.value) {
    fetchAISummary()
  }
}

const closeModal = () => {
  resetModal()
  window.modalCitizen.closeModalPortal()
}

async function handleCompleteMeeting() {
  try {
    await api.completeMeeting(
      props.remarkIds,
      props.meetingId,
      meetingSummary.value,
      meetingTitle.value,
      meetingDate.value
    )

    window.eventHub.$emit('refreshCommunicationData')

    if (props.edit) {
      window.eventHub.$emit('refreshClientMeetings')
    }

    resetModal()
    closeModal()
  } catch {
    errorMessage.value = I18n.t(
      'client_meetings.meeting_preparation.complete_meeting_modal.error'
    )
  }
}

async function fetchAISummary() {
  try {
    summaryLoading.value = true

    const response = await api.getAISummary(props.remarkIds)

    meetingSummary.value = response ? response['summary'] : ''
  } catch (e) {
    SfLog.warn('Error fetching AI summary', {
      extra: {
        error: e,
      },
    })
  } finally {
    summaryLoading.value = false
  }
}

function resetModal() {
  meetingTitle.value = ''
  meetingSummary.value = ''
  meetingDate.value = null
  errorMessage.value = null
  meetingIdRef.value = null
  remarkIdsRef.value = []
}
</script>
